import { memo, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { ThemeToggle } from '@/shared/components/MenuBar/ThemeToggle';
import { Page } from '@/features/pages/types/page.types';
import Header from '@/features/pages/components/layout/Sidebar/Header';
import { Separator } from '@/shared/components/ui/separator';
import { NoCollections } from '@/features/collections/components/CollectionsPage';
import CollectionCard from '@/features/public/components/PublicCollections/CollectionCard';
import CollectionBanner from '@/features/public/components/PublicCollections/Banner';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Card } from '@/shared/components/ui/card';
import { usePublicCollections } from '../../hooks/usePublicCollections';

const CollectionSkeleton = () => {
  return (
    <div className="mb-12">
      {Array(5)
        .fill(0)
        .map((_, i) => (
          <Card
            key={i}
            className="flex items-start justify-between rounded-lg hover:shadow-md hover:scale-[1.01] hover:bg-secondary/30 cursor-pointer transition-all duration-200 bg-background dark:bg-secondary my-2"
          >
            <div className="flex items-center gap-3 truncate">
              <Skeleton className="w-12 h-12 rounded-md opacity-80 mx-2" />
              <div className="flex flex-col overflow-hidden pb-2 pt-4">
                <Skeleton className="h-6 w-44 mb-1" />
                <Skeleton className="h-3 w-100 mt-1" />
                <Skeleton className="h-3 w-20 mt-1" />
              </div>
            </div>
            <div className="shrink-0 px-4 pt-2.5">
              <Skeleton className="h-5 w-5 rounded-full" />
            </div>
          </Card>
        ))}
    </div>
  );
};

const PublicCollections = () => {
  const { data: collections, isLoading, isError } = usePublicCollections();

  if (isError) {
    return (
      <>
        <Menu />

        <div className="h-[calc(100vh-400px)] flex items-center justify-center">
          <div className="flex-col space-y-2 text-center">
            <div className="text-3xl text-muted-foreground">¯/_(ツ)_/¯</div>
            <div className="text-lg text-muted-foreground pt-4">
              Sorry, couldn't find what you're looking for.
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="h-screen overflow-auto">
      <Menu />

      <div className="px-4 md:px-8 mx-auto md:max-w-3xl lg:max-w-6xl">
        <CollectionBanner />
        <Separator className="mt-6 mb-12" />
        {isLoading ? (
          <CollectionSkeleton />
        ) : !collections?.length ? (
          <NoCollections loading={false} />
        ) : (
          <div className="mb-20">
            {collections.map(collection => (
              <CollectionCard key={collection.id} collection={collection} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(PublicCollections);

const Menu = ({ page }: { page?: Page }) => {
  const [showTitle, setShowTitle] = useState(false);
  const lastScrollTop = useRef(0);
  const menuRef = useRef<HTMLElement | null>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    const handleScroll = () => {
      if (!menuRef.current) return;

      const scrollContainer = menuRef.current.closest('.overflow-y-auto');
      if (!scrollContainer) return;

      const scrollTop = scrollContainer.scrollTop;
      if (scrollTop > lastScrollTop.current && scrollTop > 60) {
        setShowTitle(true);
      } else if (scrollTop <= 60) {
        setShowTitle(false);
      }
      lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
    };

    const scrollContainer = menuRef.current?.closest('.overflow-y-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <nav ref={menuRef} className="sticky z-50 top-0 w-full bg-background pr-4">
      <div className="flex items-center justify-between md:px-0 w-full">
        {(!isMobile || !showTitle) && <Header />}
        {showTitle && page && (
          <div className="truncate text-md font-medium transition-opacity duration-300 ease-in-out md:max-w-3xl lg:max-w-4xl flex-1 px-2 md:p-0">
            {page.title} {page.archivedAt ? '(Archived)' : ''}
          </div>
        )}
        <ThemeToggle />
      </div>
    </nav>
  );
};
